/**
 * Sound, which so far is one sound: a siren.
 *
 * ## It is synthesised, not a file
 *
 * A siren is two tones and a sweep, which the Web Audio API makes directly and
 * exactly. Shipping a recording would mean finding one that is actually free to
 * use, carrying it in the repo, and loading it before anything can be heard --
 * all to reproduce something an oscillator does in nine lines. The synthesised
 * one also does the thing a recording cannot: it holds a continuous pitch sweep
 * for as long as a unit is on a call, without a loop point to hide.
 *
 * The pattern is the American wail: a slow rise and fall between about 700 and
 * 1,700 Hz, roughly every second and a half. Not the European two-tone, because
 * this county is in Wisconsin.
 *
 * ## How far away it is, is the whole point
 *
 * §4 draws the line between what a person can perceive and what the network
 * records, and this is on the person's side of it. A siren three blocks away is
 * information you get for free, without a query, without a plate, and it fades
 * with distance the way everything a person notices does. It is the cheapest
 * possible demonstration of the contrast the game is built on.
 *
 * So the volume is distance, squared, out to about six hundred meters. Beyond
 * that a unit is simply not audible, which is also true.
 *
 * ## Browsers will not make noise until they are asked
 *
 * Every browser blocks audio until a user gesture, which is correct and is why
 * nothing here initialises until the first click or key. Until then this is
 * inert and costs nothing.
 */

/** How far a siren carries, in meters. */
const EARSHOT = 620;

/** How many can be heard at once. Beyond this it is noise rather than sirens. */
const VOICES = 3;

export interface SoundSource {
  id: number;
  x: number;
  y: number;
}

interface Voice {
  osc: OscillatorNode;
  sweep: OscillatorNode;
  sweepGain: GainNode;
  gain: GainNode;
  /** Which vehicle this voice is currently the sound of, or null. */
  holder: number | null;
}

export class Sound {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private voices: Voice[] = [];
  private started = false;

  /**
   * Whether sound is running at all.
   *
   * Not a setting anybody chose. It is on, and it is only false for the moment
   * between construction and the first user gesture, because browsers will not
   * make noise before one.
   */
  on = false;

  /**
   * Wire up the first-gesture handler.
   *
   * Nothing is created here: an AudioContext made before a gesture starts
   * suspended, and a suspended context that nobody resumes is a silent bug that
   * only shows up on somebody else's browser.
   */
  listenForGesture(): void {
    const wake = (): void => {
      if (this.on) void this.begin();
    };
    window.addEventListener('pointerdown', wake, { passive: true });
    window.addEventListener('keydown', wake, { passive: true });
  }

  async begin(): Promise<void> {
    if (this.started) {
      if (this.context?.state === 'suspended') await this.context.resume();
      return;
    }
    this.started = true;

    const context = new AudioContext();
    this.context = context;
    const master = context.createGain();
    master.gain.value = 0.5;
    master.connect(context.destination);
    this.master = master;

    for (let i = 0; i < VOICES; i++) {
      // The tone. A sawtooth rather than a sine, because a siren is a horn and
      // a horn has harmonics; a pure sine reads as a test tone.
      const osc = context.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 1200;

      // The wail: a slow oscillator driving the pitch of the fast one.
      const sweep = context.createOscillator();
      sweep.type = 'sine';
      sweep.frequency.value = 0.66;
      const sweepGain = context.createGain();
      sweepGain.gain.value = 500;
      sweep.connect(sweepGain).connect(osc.frequency);

      const gain = context.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(master);

      osc.start();
      sweep.start();
      this.voices.push({ osc, sweep, sweepGain, gain, holder: null });
    }
  }

  setOn(on: boolean): void {
    this.on = on;
    if (!on) {
      this.silence();
      return;
    }
    void this.begin();
  }

  private silence(): void {
    for (const voice of this.voices) {
      voice.gain.gain.value = 0;
      voice.holder = null;
    }
  }

  /**
   * Point the voices at the nearest sirens and set how loud each one is.
   *
   * Voices are assigned to the nearest sources rather than to whichever arrived
   * first, and a voice keeps its holder when it can, so a unit driving past does
   * not jump between voices and click.
   */
  update(listener: { x: number; y: number } | null, sources: readonly SoundSource[]): void {
    if (!this.on || !this.context || this.voices.length === 0) return;
    if (!listener) {
      this.silence();
      return;
    }

    const near = sources
      .map((source) => ({
        source,
        away: Math.hypot(source.x - listener.x, source.y - listener.y),
      }))
      .filter((entry) => entry.away < EARSHOT)
      .sort((a, b) => a.away - b.away)
      .slice(0, VOICES);

    const wanted = new Set(near.map((entry) => entry.source.id));
    for (const voice of this.voices) {
      if (voice.holder !== null && !wanted.has(voice.holder)) voice.holder = null;
    }

    for (const entry of near) {
      let voice = this.voices.find((one) => one.holder === entry.source.id);
      if (!voice) {
        voice = this.voices.find((one) => one.holder === null);
        if (!voice) continue;
        voice.holder = entry.source.id;
      }
      // Squared falloff, which is how sound actually thins out, and it keeps a
      // unit two streets away from being as loud as one behind you.
      const near01 = 1 - entry.away / EARSHOT;
      const target = near01 * near01 * 0.34;
      // Ramped rather than set, or every snapshot is a click.
      voice.gain.gain.setTargetAtTime(target, this.context.currentTime, 0.08);
    }

    for (const voice of this.voices) {
      if (voice.holder !== null) continue;
      voice.gain.gain.setTargetAtTime(0, this.context.currentTime, 0.1);
    }
  }
}
